import { useCallback, useEffect, useRef } from "react";
import { GifPlaybackEvent, metricsBus, EventSource } from "@/common/buses";
import { IEventSource, useAnalyticsStore } from "@/common/store/analyticsStore";

const MS_IN_SEC = 1000;

interface IProps {
  page: "watch" | "gifs" | "niche";
}

export const usePageClosedEvent = ({ page }: IProps) => {
  const gifsViewed = useRef<number>(0);
  const gifsViewedMeaningful = useRef<number>(0);
  const timeSpent = useRef<number>(0);
  const contentLength = useRef<number>(0);

  const incrementGifsViewed = useCallback(() => {
    gifsViewed.current++;
  }, []);

  const incrementGifsViewedMeaningful = useCallback(() => {
    gifsViewedMeaningful.current++;
  }, []);

  const incrementPlayback = useCallback(({ duration, gifDuration }: GifPlaybackEvent) => {
    timeSpent.current += duration / MS_IN_SEC;
    contentLength.current += gifDuration ?? 0;
  }, []);

  const canSendEvent = (eventSource: IEventSource) => {
    return [
      EventSource.SearchGifsTop,
      EventSource.SearchGifsTrending,
      EventSource.SearchGifsLatest,
      EventSource.SearchGifsHot,
      EventSource.Search,
      EventSource.Niche
    ].includes(eventSource.name);
  };

  const sendEvent = (eventSource: IEventSource, searchTag?: string) => {
    metricsBus.emit("pageClosed", {
      page,
      type: eventSource.name,
      gifsViewed: gifsViewed.current,
      gifsViewedMeaningful: gifsViewedMeaningful.current,
      timeSpent: timeSpent.current,
      contentLength: contentLength.current,
      searchTag,
    });
  };

  const getEventSource = () => {
    return useAnalyticsStore.getState().eventSource;
  };

  const getSearchTag = () => {
    return useAnalyticsStore.getState().searchTag ?? undefined;
  };

  const beforeUnload = () => {
    const eventSource = getEventSource();

    if (eventSource !== null && canSendEvent(eventSource)) {
      sendEvent(eventSource, getSearchTag());
    }
  };

  useEffect(() => {
    metricsBus.on("gifView", incrementGifsViewed);
    metricsBus.on("meaningfulGifView", incrementGifsViewedMeaningful);
    metricsBus.on("gifPlayback", incrementPlayback);
    window.addEventListener("beforeunload", beforeUnload);

    return () => {
      const eventSource = getEventSource();
      const searchTag = getSearchTag();

      setTimeout(() => {
        metricsBus.off("gifView", incrementGifsViewed);
        metricsBus.off("meaningfulGifView", incrementGifsViewedMeaningful);
        metricsBus.off("gifPlayback", incrementPlayback);
        window.removeEventListener("beforeunload", beforeUnload);

        if (eventSource !== null && canSendEvent(eventSource)) {
          sendEvent(eventSource, searchTag);
        }
      });
    };
  }, []);
};
